import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { claimsAPI } from '../services/apiService';

const EMPTY_STATS = {
  totalMembers: 0,
  totalClaims: 0,
  completedClaims: 0,
  pendingClaims: 0,
  inProgressClaims: 0,
  totalFees: 0,
  totalAdvance: 0,
  overdueCount: 0,
  overdueClaims: []
};

const readCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem('pf_dashboard_stats') || 'null');
  } catch {
    return null;
  }
};

const Dashboard = () => {
  const cached = readCache();
  const [stats, setStats] = useState(() => cached || EMPTY_STATS);
  const [loading, setLoading] = useState(!cached);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      if (cached) setRefreshing(true);
      else setLoading(true);
      try {
        const statsData = await claimsAPI.getStats();
        if (!cancelled) {
          setStats(statsData);
          sessionStorage.setItem('pf_dashboard_stats', JSON.stringify(statsData));
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, []);

  const statCards = [
    {
      name: 'Total Members',
      value: stats.totalMembers,
      icon: Users,
      color: 'bg-primary-500',
      link: '/adminportal/members'
    },
    {
      name: 'Completed Claims',
      value: stats.completedClaims,
      icon: CheckCircle,
      color: 'bg-green-600',
      link: '/adminportal/members'
    },
    {
      name: 'In Progress',
      value: stats.inProgressClaims,
      icon: Clock,
      color: 'bg-amber-500',
      link: '/adminportal/members'
    },
    {
      name: 'Pending Claims',
      value: stats.pendingClaims,
      icon: AlertCircle,
      color: 'bg-red-500',
      link: '/adminportal/members'
    },
    {
      name: 'Overdue Claims',
      value: stats.overdueCount || 0,
      icon: AlertTriangle,
      color: 'bg-orange-600',
      link: '#overdue'
    },
    {
      name: 'Total Fees',
      value: `₹${(stats.totalFees || 0).toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'bg-violet-500',
      link: '/adminportal/members'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">
          Overview of your PF Claims Management system
          {refreshing ? <span className="ml-2 text-primary-600 text-sm">Refreshing…</span> : null}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const Comp = stat.link.startsWith('#') ? 'a' : Link;
          const props = stat.link.startsWith('#')
            ? { href: stat.link }
            : { to: stat.link };
          return (
            <Comp
              key={stat.name}
              {...props}
              className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </Comp>
          );
        })}
      </div>

      <div id="overdue" className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Overdue claims</h3>
        <p className="text-xs text-slate-500 mb-4">
          Based on claim creation date + quoted timeline working days. Incomplete claims past that due date appear here.
        </p>
        {(stats.overdueClaims || []).length === 0 ? (
          <p className="text-sm text-slate-500">No overdue claims.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
                  <th className="py-2 pr-3">Claim</th>
                  <th className="py-2 pr-3">Member</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3">Timeline</th>
                  <th className="py-2 pr-3">Due</th>
                  <th className="py-2 pr-3">Overdue</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.overdueClaims.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-violet-50/40">
                    <td className="py-2 pr-3">
                      <Link className="text-primary-700 font-mono font-semibold" to={`/adminportal/members/${c.member_id}`}>
                        #{c.id}
                      </Link>
                    </td>
                    <td className="py-2 pr-3">{c.member_name}</td>
                    <td className="py-2 pr-3">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="py-2 pr-3">{c.timelineDays} WD</td>
                    <td className="py-2 pr-3">{c.dueDate}</td>
                    <td className="py-2 pr-3 text-orange-700 font-medium">{c.daysOverdue}d</td>
                    <td className="py-2">{c.final_status || 'Not Started'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/adminportal/members/new"
              className="flex items-center p-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors duration-200"
            >
              <Users className="w-5 h-5 text-primary-600 mr-3" />
              <span className="text-primary-700 font-medium">Add New Member</span>
            </Link>
            <Link
              to="/adminportal/members"
              className="flex items-center p-3 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors duration-200"
            >
              <TrendingUp className="w-5 h-5 text-violet-600 mr-3" />
              <span className="text-violet-700 font-medium">View All Members</span>
            </Link>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Total Fees Collected</span>
              <span className="font-semibold text-green-600">₹{(stats.totalFees || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">Total Advance Given</span>
              <span className="font-semibold text-primary-600">₹{(stats.totalAdvance || 0).toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
